import {
  useEffect,
  useState,
  useMemo,
  InputHTMLAttributes,
  LabelHTMLAttributes,
  ChangeEvent,
} from 'react';
import { createUseStyles, useDarkMode } from '@glu/theming';
import { withInputAPI } from '@glu/validation-react';
import { CheckmarkAshIcon } from '@glu/icons-react';
import classnames from 'classnames';
import LabelText, { ILabelTextProps } from '../LabelText/LabelText';
import styles from './CheckboxRadio.styles';
import Error, { IErrorProps } from '../Error/Error';

export interface ICheckboxRadioProps
  extends Pick<IErrorProps, 'error'>,
    Pick<ILabelTextProps, 'screenReaderLabel' | 'tooltipContent'>,
    Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  /** Determines whether checkbox is checked */
  checked?: boolean;
  /** Classname to apply to container label element */
  className?: string;
  /** Applies dark mode styles */
  dark?: boolean;
  /** Array of all current errors */
  errors?: IErrorProps['error'][];
  /** Id applied to the input. If not provided id will be `checkbox-radio-${uniqueId++}` */
  htmlId?: string;
  /** element value */
  htmlValue?: string | number | readonly string[];
  /** Render checkbox in an indeterminate state */
  indeterminate?: boolean;
  /** Determines if the label will be shown with inline styling */
  inline?: boolean;
  /** Classname to apply to checkbox input */
  inputClass?: string;
  labelClass?: ILabelTextProps['className'];
  /** Properties to pass to the label tag */
  labelTagProps?: LabelHTMLAttributes<HTMLLabelElement>;
  labelText: ILabelTextProps['label'];
  /** form field name */
  name: string;
  /** Custom on change handler for the checkbox */
  onChange(
    name: string,
    value: ICheckboxRadioProps['htmlValue'] | boolean,
    fromBlur?: boolean
  ): void;
  /** Determines if type is "checkbox" or "radio" */
  type?: 'checkbox' | 'radio';
}

const useStyles = createUseStyles(styles);

let uniqueId = 0;

const CheckboxRadio = (props: ICheckboxRadioProps) => {
  const {
    checked,
    className,
    dark: propDark,
    disabled,
    error,
    errors,
    htmlId,
    htmlValue = true,
    indeterminate = false,
    inline,
    inputClass,
    labelClass,
    labelTagProps,
    labelText,
    name,
    onChange,
    onKeyDown,
    screenReaderLabel,
    tooltipContent,
    type = 'checkbox',
    value: fieldValue = false,
    ...rest
  } = props;
  const darkMode = useDarkMode();
  const dark = propDark || darkMode;
  const classes = useStyles();

  // eslint-disable-next-line no-plusplus
  const id = useMemo(() => htmlId || `checkbox-radio-${uniqueId++}`, [htmlId]);
  const errorId = useMemo(() => `error-${id}`, [id]);
  const [isSelected, setIsSelected] = useState(
    checked === undefined ? htmlValue === fieldValue : checked
  );

  const errorMessage = error || errors?.[0];

  useEffect(() => {
    setIsSelected(checked === undefined ? htmlValue === fieldValue : checked);
  }, [checked, fieldValue, htmlValue]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setIsSelected(isChecked);
    onChange(name, isChecked ? htmlValue : isChecked);
  };

  return (
    <label
      className={classnames(
        classes.root,
        { [classes.dark]: dark, [classes.disabled]: disabled, [classes.inline]: inline },
        className
      )}
      htmlFor={id}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...labelTagProps}
    >
      <input
        ref={el => el && (el.indeterminate = indeterminate)} // eslint-disable-line
        aria-errormessage={errorMessage ? errorId : undefined}
        aria-invalid={!!errorMessage}
        checked={isSelected}
        className={inputClass}
        disabled={disabled}
        id={id}
        name={name}
        onChange={handleChange}
        onKeyDown={onKeyDown}
        type={type}
        // htmlValue is a boolean which is not valid for a value. We will come back and fix this.
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        value={htmlValue}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...rest}
      />
      <span
        className={`
          ${classes.icon}
          ${type === 'checkbox' ? classes.checkboxIcon : classes.radioIcon}
          ${isSelected ? classes.selected : ''}
        `}
      >
        {type === 'checkbox' ? (
          indeterminate ? (
            <span className={classes.dash}>–</span>
          ) : (
            isSelected && <CheckmarkAshIcon />
          )
        ) : (
          <span className={classes.radioDot} />
        )}
      </span>
      <LabelText
        checkboxLabel
        className={labelClass}
        hasError={!!errorMessage}
        label={labelText}
        screenReaderLabel={screenReaderLabel}
        tooltipContent={tooltipContent}
      />
      <Error error={errorMessage} htmlId={errorId} inCheckboxRadio />
    </label>
  );
};

const WithInputCheckboxRadio = withInputAPI(CheckboxRadio);
export const BareCheckboxRadio = CheckboxRadio;
export default WithInputCheckboxRadio;
export const ValidatedCheckboxRadio = WithInputCheckboxRadio;
